import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { UtensilsCrossed, Plus, Search, Trash2, Heart, CheckCircle2, Star, ExternalLink, MessageSquare, ImagePlus, X, Share2, Copy } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";

const STATE_META = {
  want: { label: "Want to try", cls: "bg-rose-50 text-rose-700 border-rose-300", icon: Heart },
  ate: { label: "Ate here", cls: "bg-emerald-50 text-emerald-700 border-emerald-300", icon: CheckCircle2 },
  favorite: { label: "Favorite", cls: "bg-amber-50 text-amber-700 border-amber-300", icon: Star },
};
const EMPTY = { name: "", url: "", destination: "", area: "", notes: "", state: "want" };

/* My Food List — hub section: guide saves + custom entries with search, comments, photos. */
export default function FoodListSection() {
  const [data, setData] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [f, setF] = useState(EMPTY);
  const [q, setQ] = useState("");
  const [results, setResults] = useState(null);
  const [openItem, setOpenItem] = useState(null); // item id with comments/photos expanded
  const [comment, setComment] = useState("");
  const [share, setShare] = useState(null); // {enabled, token}
  const [showShare, setShowShare] = useState(false);
  const fileRef = useRef(null);

  const load = useCallback(() => api.get("/food-list").then((r) => setData(r.data)).catch(() => setData({ items: [], guide_items: [] })), []);
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (q.trim().length < 2) { setResults(null); return undefined; }
    const t = setTimeout(() => api.get("/food-list/search", { params: { q } }).then((r) => setResults(r.data)).catch(() => {}), 500);
    return () => clearTimeout(t);
  }, [q]);

  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const save = async () => {
    if (!f.name.trim()) { toast.error("Name is required"); return; }
    try { await api.post("/food-list", f); toast.success("Added to your food list"); setShowForm(false); setF(EMPTY); load(); }
    catch { toast.error("Could not add"); }
  };
  const addFromSearch = async (pl) => {
    try { await api.post("/food-list", { name: pl.name, place_id: pl.id, destination: pl.slug, area: pl.area, notes: pl.known_for, state: "want" });
      toast.success(`${pl.name} added to your list`); setQ(""); setResults(null); load(); }
    catch { toast.error("Could not add"); }
  };
  const setState = async (item, state) => {
    try { await api.patch(`/food-list/${item.id}`, { state }); load(); } catch { toast.error("Could not update"); }
  };
  const del = async (id) => {
    if (!window.confirm("Remove from your food list?")) return;
    try { await api.delete(`/food-list/${id}`); load(); } catch { toast.error("Could not remove"); }
  };
  const addComment = async (id) => {
    if (!comment.trim()) return;
    try { await api.post(`/food-list/${id}/comments`, { text: comment }); setComment(""); load(); } catch { toast.error("Could not add note"); }
  };
  const delComment = async (id, cid) => { try { await api.delete(`/food-list/${id}/comments/${cid}`); load(); } catch { /* noop */ } };
  const [photoNote, setPhotoNote] = useState("");
  const uploadPhotos = async (id, files, caption) => {
    let ok = 0;
    for (const file of Array.from(files)) {
      const fd = new FormData(); fd.append("file", file);
      if (caption?.trim()) fd.append("caption", caption.trim());
      try { await api.post(`/food-list/${id}/photos`, fd, { headers: { "Content-Type": "multipart/form-data" } }); ok += 1; }
      catch (e) { toast.error(e?.response?.data?.detail || "Upload failed"); }
    }
    if (ok) { toast.success(ok > 1 ? `${ok} photos added` : "Photo added"); setPhotoNote(""); load(); }
  };
  const delPhoto = async (id, pid) => { try { await api.delete(`/food-list/${id}/photos/${pid}`); load(); } catch { /* noop */ } };

  const openShare = async () => {
    setShowShare((v) => !v);
    if (!share) { try { setShare((await api.get("/food-list/share")).data); } catch { /* noop */ } }
  };
  const toggleShare = async (enabled) => {
    try { setShare((await api.post("/food-list/share", { enabled })).data);
      toast.success(enabled ? "Sharing is on — send the link to your trip companions" : "Sharing turned off"); }
    catch { toast.error("Could not update sharing"); }
  };
  const shareUrl = share?.token ? `${window.location.origin}/food-list/shared/${share.token}` : "";
  const copyShare = async () => { try { await navigator.clipboard.writeText(shareUrl); toast.success("Link copied"); } catch { toast.error("Could not copy"); } };

  const inp = "border border-zinc-300 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-primary";
  const items = data?.items || [];
  const guideItems = data?.guide_items || [];

  const StateButtons = ({ item }) => (
    <span className="flex gap-1">
      {Object.entries(STATE_META).map(([s, m]) => (
        <button key={s} onClick={() => setState(item, s)} title={m.label} data-testid={`foodlist-state-${s}-${item.id}`}
          className={`inline-flex items-center gap-1 text-[11px] font-700 px-1.5 py-0.5 border rounded-full ${item.state === s ? m.cls : "border-zinc-200 text-zinc-400 hover:text-zinc-700"}`}>
          <m.icon className="w-3 h-3" />
        </button>
      ))}
    </span>
  );

  return (
    <div data-testid="foodlist-section">
      <div className="flex flex-wrap items-center gap-3 mb-1">
        <h2 className="font-heading font-900 text-orange-700 text-2xl flex items-center gap-2"><UtensilsCrossed className="w-6 h-6" /> My Food List</h2>
        <button onClick={() => setShowForm((v) => !v)} data-testid="foodlist-add-btn"
          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-700 text-white bg-orange-600 hover:bg-orange-700 transition-colors">
          <Plus className="w-4 h-4" /> Add a place
        </button>
        <button onClick={openShare} data-testid="foodlist-share-btn"
          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-700 border border-orange-600 text-orange-700 hover:bg-orange-50 transition-colors">
          <Share2 className="w-4 h-4" /> Share list
        </button>
      </div>
      {showShare && (
        <div className="border border-orange-200 bg-orange-50/40 p-4 mb-4 space-y-2" data-testid="foodlist-share-panel">
          <p className="text-sm text-zinc-700">Share your food list with trip companions — they get a read-only link so everyone eats well together.</p>
          {share?.enabled ? (
            <>
              <div className="flex flex-wrap items-center gap-2">
                <input readOnly value={shareUrl} data-testid="foodlist-share-url"
                  className="flex-1 min-w-[220px] border border-zinc-300 px-3 py-2 text-xs bg-white" onFocus={(e) => e.target.select()} />
                <button onClick={copyShare} data-testid="foodlist-share-copy"
                  className="inline-flex items-center gap-1.5 px-3 py-2 text-xs font-700 text-white bg-orange-600 hover:bg-orange-700">
                  <Copy className="w-3.5 h-3.5" /> Copy link
                </button>
                <a href={`https://wa.me/?text=${encodeURIComponent(`Here's my food list for our trip — let's eat well together! ${shareUrl}`)}`}
                  target="_blank" rel="noreferrer" data-testid="foodlist-share-whatsapp"
                  className="inline-flex items-center gap-1.5 px-3 py-2 text-xs font-700 text-white bg-emerald-600 hover:bg-emerald-700">
                  WhatsApp
                </a>
              </div>
              <button onClick={() => toggleShare(false)} data-testid="foodlist-share-off" className="text-xs text-muted-foreground hover:text-destructive underline underline-offset-2">
                Turn sharing off
              </button>
            </>
          ) : (
            <button onClick={() => toggleShare(true)} data-testid="foodlist-share-on"
              className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-700 text-white bg-orange-600 hover:bg-orange-700">
              <Share2 className="w-4 h-4" /> Create share link
            </button>
          )}
        </div>
      )}
      <p className="text-sm text-muted-foreground mb-4">Restaurants and food finds you want to try or loved — from our guides, our site or the web.</p>

      <div className="relative max-w-md mb-4">
        <Search className="w-4 h-4 absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
        <input value={q} onChange={(e) => setQ(e.target.value)} data-testid="foodlist-search"
          placeholder="Search restaurants & markets on TravelerPulse…" className={`w-full pl-8 ${inp}`} />
        {results && (
          <div className="absolute z-20 top-full left-0 right-0 bg-white border border-zinc-300 shadow-lg max-h-72 overflow-y-auto" data-testid="foodlist-search-results">
            {(results.places || []).map((pl) => (
              <button key={pl.id} onClick={() => addFromSearch(pl)} data-testid={`foodlist-result-${pl.id}`}
                className="w-full text-left px-3 py-2 hover:bg-orange-50 text-sm border-b border-zinc-100">
                <b>{pl.name}</b> <span className="text-muted-foreground text-xs">{pl.type} · {pl.area || pl.slug}</span>
              </button>
            ))}
            {(results.places || []).length === 0 && <div className="px-3 py-2 text-sm text-muted-foreground">No matches on TravelerPulse.</div>}
            <a href={results.google_url} target="_blank" rel="noreferrer" data-testid="foodlist-google-link"
              className="block px-3 py-2 text-sm text-primary hover:bg-zinc-50 font-700">Search Google for “{q}” → then add it with “Add a place”</a>
          </div>
        )}
      </div>

      {showForm && (
        <div className="border border-orange-200 bg-orange-50/40 p-4 mb-5 space-y-2" data-testid="foodlist-form">
          <input value={f.name} onChange={set("name")} data-testid="foodlist-name" placeholder="Place name (e.g. Dhe Puttu)" className={`w-full ${inp}`} />
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            <input value={f.destination} onChange={set("destination")} data-testid="foodlist-destination" placeholder="Destination" className={inp} />
            <input value={f.area} onChange={set("area")} placeholder="Area" className={inp} />
            <input value={f.url} onChange={set("url")} data-testid="foodlist-url" placeholder="Link (website / Google Maps)" className={inp} />
            <select value={f.state} onChange={set("state")} data-testid="foodlist-state" className={`${inp} bg-white`}>
              {Object.entries(STATE_META).map(([s, m]) => <option key={s} value={s}>{m.label}</option>)}
            </select>
          </div>
          <textarea value={f.notes} onChange={set("notes")} data-testid="foodlist-notes" rows={2} placeholder="Notes — what to order, who recommended it…" className={`w-full ${inp} resize-y`} />
          <button onClick={save} data-testid="foodlist-save" className="px-4 py-2 text-sm font-700 text-white bg-orange-600 hover:bg-orange-700">Save to my list</button>
        </div>
      )}

      {!data ? <p className="text-sm text-muted-foreground">Loading…</p> : (
        <>
          {items.length === 0 && guideItems.length === 0 && (
            <p className="text-sm text-muted-foreground border border-dashed border-zinc-300 p-6 text-center" data-testid="foodlist-empty">
              Nothing saved yet — search above, add a place manually, or tap <b>Want to try</b> on any <Link to="/food-dining/kochi" className="text-primary hover:underline">Food &amp; Dining guide</Link>.
            </p>
          )}
          <div className="space-y-2" data-testid="foodlist-items">
            {items.map((m) => (
              <div key={m.id} className="border border-zinc-300 bg-white p-3.5" data-testid={`foodlist-item-${m.id}`}>
                <div className="flex flex-wrap items-center gap-2">
                  <span className="font-700">{m.name}</span>
                  {m.destination && <span className="text-xs text-muted-foreground">{m.destination}{m.area ? ` · ${m.area}` : ""}</span>}
                  {m.url && <a href={m.url} target="_blank" rel="noreferrer" className="text-primary" data-testid={`foodlist-link-${m.id}`}><ExternalLink className="w-3.5 h-3.5" /></a>}
                  <span className="ml-auto flex items-center gap-2">
                    <StateButtons item={m} />
                    <button onClick={() => setOpenItem(openItem === m.id ? null : m.id)} data-testid={`foodlist-open-${m.id}`}
                      className="text-muted-foreground hover:text-orange-700 inline-flex items-center gap-1 text-xs">
                      <MessageSquare className="w-3.5 h-3.5" /> {(m.comments || []).length + (m.photos || []).length || ""}
                    </button>
                    <button onClick={() => del(m.id)} data-testid={`foodlist-delete-${m.id}`} className="text-muted-foreground hover:text-destructive"><Trash2 className="w-4 h-4" /></button>
                  </span>
                </div>
                {m.notes && <p className="text-sm text-zinc-700 mt-1">{m.notes}</p>}
                {(m.photos || []).length > 0 && (
                  <div className="flex gap-2 mt-2 flex-wrap">
                    {m.photos.map((ph) => (
                      <div key={ph.id} className="relative group">
                        <img src={`${process.env.REACT_APP_BACKEND_URL}${ph.url}`} alt={ph.caption || ""} title={ph.caption || ""} className="w-20 h-20 object-cover border border-zinc-200" />
                        {ph.caption && <p className="text-[10px] text-muted-foreground w-20 truncate" title={ph.caption}>{ph.caption}</p>}
                        <button onClick={() => delPhoto(m.id, ph.id)} className="absolute -top-1.5 -right-1.5 bg-white border rounded-full p-0.5 opacity-0 group-hover:opacity-100"><X className="w-3 h-3" /></button>
                      </div>
                    ))}
                  </div>
                )}
                {openItem === m.id && (
                  <div className="mt-2 pt-2 border-t border-zinc-100 space-y-1.5" data-testid={`foodlist-detail-${m.id}`}>
                    {(m.comments || []).map((c) => (
                      <div key={c.id} className="text-sm flex items-start gap-2">
                        <span className="flex-1">{c.text}</span>
                        <button onClick={() => delComment(m.id, c.id)} className="text-muted-foreground hover:text-destructive"><X className="w-3 h-3" /></button>
                      </div>
                    ))}
                    <div className="flex items-center gap-2">
                      <input value={comment} onChange={(e) => setComment(e.target.value)} onKeyDown={(e) => e.key === "Enter" && addComment(m.id)}
                        data-testid={`foodlist-comment-input-${m.id}`} placeholder="Add a note or comment…" className={`flex-1 ${inp} py-1.5`} />
                      <button onClick={() => addComment(m.id)} data-testid={`foodlist-comment-add-${m.id}`} className="px-3 py-1.5 text-xs font-700 border border-zinc-300 hover:border-orange-500">Add</button>
                    </div>
                    <div className="flex items-center gap-2">
                      <input value={photoNote} onChange={(e) => setPhotoNote(e.target.value)}
                        data-testid={`foodlist-photo-note-${m.id}`} placeholder="Photo title / note (optional)…" className={`flex-1 ${inp} py-1.5`} />
                      <label className="cursor-pointer inline-flex items-center gap-1 text-xs font-700 text-orange-700 border border-orange-300 bg-white px-2.5 py-1.5 hover:bg-orange-50" title="Add photos">
                        <ImagePlus className="w-4 h-4" /> Add photos
                        <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp" multiple className="hidden"
                          data-testid={`foodlist-photo-input-${m.id}`}
                          onChange={(e) => { const files = Array.from(e.target.files || []); e.target.value = ""; if (files.length) uploadPhotos(m.id, files, photoNote); }} />
                      </label>
                    </div>
                  </div>
                )}
              </div>
            ))}
            {guideItems.map((g) => (
              <div key={g.place_id} className="border border-dashed border-orange-300 bg-orange-50/30 p-3.5" data-testid={`foodlist-guide-${g.place_id}`}>
                <div className="flex flex-wrap items-center gap-2">
                  <span className="font-700">{g.name}</span>
                  <span className="text-xs text-muted-foreground">{g.destination}{g.area ? ` · ${g.area}` : ""}</span>
                  <span className={`text-[11px] font-700 px-1.5 py-0.5 border rounded-full ${STATE_META[g.state]?.cls || ""}`}>{STATE_META[g.state]?.label}</span>
                  <Link to={`/food-dining/${g.destination}`} className="text-xs text-primary hover:underline ml-auto">open guide →</Link>
                </div>
                {g.notes && <p className="text-sm text-zinc-700 mt-1">{g.notes}</p>}
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
